import mapboxgl, { LngLatBounds, Map, Marker, NavigationControl, Popup } from 'mapbox-gl';
import { locale } from './map/translations/de';

const {
    accessToken = null,
    mapboxStyleUsername = null,
    mapboxStyleId = null,
} = window.mapbox_config;

mapboxgl.accessToken = accessToken;

const USER_LOCATION_LAYER_ID = 'user-location-layer';
const USER_LOCATION_SOURCE_ID = 'user-location-source';

const germany = {
    center: [10.4515, 51.1657],
    zoom: 6,
};

const currentMarkersByMap = new WeakMap();

const mapBoundsForFeatures = features => {
    const bounds = features.reduce(
        (boundsObject, feature) => boundsObject.extend(feature.geometry.coordinates),
        new LngLatBounds(),
    );
    return bounds;
};

const createMarkersFromGeoJSON = geojson => {
    return geojson.features.map(feature => {
        const marker = new Marker({ color: '#c23219' }).setLngLat(feature.geometry.coordinates);
        if (!feature.properties?.popupHTML) {
            return marker;
        }

        const popup = new Popup({
            className: 'map-popup-container',
            closeButton: false,
            maxWidth: 'none',
        })
            .setLngLat(feature.geometry.coordinates)
            .setHTML(feature.properties?.popupHTML);

        marker.setPopup(popup);
        return marker;
    });
};

const initializeMap = mount => {
    const geojson = mount.dataset.mapGeojson ? JSON.parse(mount.dataset.mapGeojson) : null;

    let areaOptions = {};
    if (!geojson || geojson.features.length === 0) {
        areaOptions = germany;
    } else if (geojson.features.length === 1) {
        areaOptions = {
            center: geojson.features[0].geometry.coordinates,
            zoom: geojson.features[0].properties?.zoom || 13,
        };
    } else if (geojson.features.length > 1) {
        areaOptions = {
            bounds: mapBoundsForFeatures(geojson.features),
            fitBoundsOptions: { padding: 80 },
        };
    }

    const map = new Map({
        container: mount,
        attributionControl: true,
        cooperativeGestures: true,
        dragRotate: false,
        touchPitch: false,
        language: 'de',
        locale,
        style: `mapbox://styles/${mapboxStyleUsername}/${mapboxStyleId}`,
        ...areaOptions,
    });
    map.addControl(new NavigationControl());

    if (geojson) {
        const markers = createMarkersFromGeoJSON(geojson);
        markers.forEach(marker => marker.addTo(map));
        currentMarkersByMap.set(map, markers);
    }

    map.on('load', () => {
        map.addSource(USER_LOCATION_SOURCE_ID, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [],
            },
        });

        map.addLayer({
            id: USER_LOCATION_LAYER_ID,
            type: 'circle',
            source: USER_LOCATION_SOURCE_ID,
            paint: {
                'circle-radius': 4,
                'circle-stroke-color': '#427af4',
                'circle-stroke-width': 8,
                'circle-color': '#fafafa',
            },
        });
    });

    return map;
};

/**
 * Update the user location indicator on the map.
 * @param {Map} map         The map instance.
 * @param {object} location An object with latitude and longitude properties.
 * @returns
 */
const updateUserLocationOnMap = (map, location) => {
    const source = map.getSource(USER_LOCATION_SOURCE_ID);
    if (!source) {
        return;
    }

    if (!location || !location.latitude || !location.longitude) {
        source.setData({
            type: 'FeatureCollection',
            features: [],
        });
        return;
    }

    source.setData({
        type: 'FeatureCollection',
        features: [
            {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [location.longitude, location.latitude],
                },
            },
        ],
    });
};

/**
 * Update map markers for the new GeoJSON data, replacing any existing markers.
 * @param {Map} map         The map instance.
 * @param {object} geojson  A GeoJSON object with features to display on the map.
 * @param {object} location The selected location
 */
const updateMapMarkers = (map, geojson, location) => {
    const currentMarkers = currentMarkersByMap.get(map);
    currentMarkers?.forEach(marker => marker.remove());

    const markers = createMarkersFromGeoJSON(geojson);
    markers.forEach(marker => marker.addTo(map));
    currentMarkersByMap.set(map, markers);

    if (geojson.features.length > 1) {
        const bounds = mapBoundsForFeatures(geojson.features);
        map.fitBounds(bounds, { duration: 750, padding: 80 });
    } else if (geojson.features.length === 1) {
        map.flyTo({
            center: geojson.features[0].geometry.coordinates,
            zoom: geojson.features[0].properties?.zoom || 13,
            duration: 750,
        });
    } else if (location) {
        map.flyTo({
            center: [location.longitude, location.latitude],
            zoom: 11,
            duration: 750,
        });
    }

    return map;
};

document.querySelectorAll('[data-map-wrapper]').forEach(wrapper => {
    const mount = wrapper.querySelector('[data-map-mount]');
    let map;

    wrapper.addEventListener(
        'onplaceholderchange',
        e => {
            if (e.detail.consent) {
                map = initializeMap(mount);
            }
        },
        { once: true, passive: true },
    );

    mount.addEventListener('mapboxupdate', e => {
        if (!map) {
            return;
        }

        let { geojson } = e.detail;
        geojson = typeof geojson === 'string' ? JSON.parse(geojson) : geojson;

        updateMapMarkers(map, geojson, e.detail.location);
        updateUserLocationOnMap(map, e.detail.location);
    });
});
